// Client-side crash reporter — Sentry edition.
//
// Replaces the homegrown `POST /api/client-errors` sink. Sentry handles:
//   - Source-map de-minification (uploaded via @sentry/vite-plugin)
//   - Breadcrumbs (XHR/fetch, console, navigation, UI clicks)
//   - Release tracking (gated on VITE_APP_VERSION)
//   - Regression alerts + dashboards
//   - Auto-installed window.error and unhandledrejection handlers
//
// Privacy contract (provider-side, in `beforeSend` / `beforeBreadcrumb`):
//   * NEVER sends mission text, postcard text, child names, or photo bytes.
//     None of those values appear in JS Error messages, stack traces, or
//     URLs in the first place; this module also strips obvious base64 /
//     data: blobs from event/breadcrumb fields as defense-in-depth.
//   * Strips query string + hash from request URLs and breadcrumbs —
//     invite codes and other low-sensitivity tokens may appear there.
//   * `sendDefaultPii: false` (the SDK default) — IP and headers are not
//     attached.
//   * No user identifier is set; we identify a session only via the
//     SDK's auto-generated, transient `session_id`.
//
// Initialization is gated on `VITE_SENTRY_DSN`. Without a DSN the module
// becomes a no-op so local dev and CI builds don't try to phone home.

import * as Sentry from "@sentry/react";

type ImportMetaEnvLike = Record<string, unknown> | undefined;

function readEnv(key: string): string | undefined {
  if (typeof import.meta === "undefined") return undefined;
  const env = (import.meta as { env?: ImportMetaEnvLike }).env;
  if (!env) return undefined;
  const v = env[key];
  return typeof v === "string" && v.length > 0 ? v : undefined;
}

const APP_VERSION = readEnv("VITE_APP_VERSION") ?? "dev";
const DSN = readEnv("VITE_SENTRY_DSN");
const ENVIRONMENT =
  readEnv("VITE_SENTRY_ENVIRONMENT") ??
  readEnv("MODE") ??
  (readEnv("PROD") === "true" ? "production" : "development");

export type ClientErrorSource =
  | "react_error_boundary"
  | "window_error"
  | "unhandled_rejection";

const DATA_URL_RE = /data:[a-zA-Z0-9.+\-/]+(?:;[a-zA-Z0-9=.+\-/]+)*,[^\s)]+/g;
const LONG_BASE64_RE = /[A-Za-z0-9+/=]{96,}/g;

function scrubText(value: string | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  return String(value)
    .replace(DATA_URL_RE, "[data-url-scrubbed]")
    .replace(LONG_BASE64_RE, "[blob-scrubbed]");
}

function stripQueryAndHash(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    const s = String(url);
    const noHash = s.split("#")[0] ?? s;
    return noHash.split("?")[0] ?? noHash;
  } catch {
    return String(url ?? null);
  }
}

// Walk an event and scrub all free-text fields that may contain
// PII or opaque blobs. Mirrors the cap behavior the homegrown sink
// used (truncate-to-bounded) but lets Sentry retain its own quota
// management for the rest.
function scrubEvent(event: Sentry.ErrorEvent): Sentry.ErrorEvent {
  if (event.message) {
    event.message = scrubText(event.message) ?? event.message;
  }
  if (event.exception?.values) {
    for (const ex of event.exception.values) {
      if (ex.value) ex.value = scrubText(ex.value) ?? ex.value;
      if (ex.type) ex.type = scrubText(ex.type) ?? ex.type;
    }
  }
  if (event.request) {
    if (typeof event.request.url === "string") {
      const cleaned = stripQueryAndHash(event.request.url);
      if (cleaned) event.request.url = cleaned;
    }
    // Drop query + cookies + headers entirely — none of them carry
    // signal we want and any of them could carry a session token.
    delete event.request.query_string;
    delete event.request.cookies;
    delete event.request.headers;
    delete event.request.data;
  }
  // Drop user identification — we never set it, and we don't want
  // Sentry's auto-IP to land here either.
  delete event.user;
  return event;
}

function scrubBreadcrumb(
  crumb: Sentry.Breadcrumb,
): Sentry.Breadcrumb | null {
  // Drop console.log/info chatter — keep only warnings & errors.
  if (crumb.category === "console") {
    if (crumb.level !== "warning" && crumb.level !== "error") return null;
  }
  if (typeof crumb.message === "string") {
    crumb.message = scrubText(crumb.message) ?? crumb.message;
  }
  if (crumb.data) {
    const data = crumb.data as Record<string, unknown>;
    if (typeof data.url === "string") {
      const cleaned = stripQueryAndHash(data.url);
      if (cleaned) data.url = cleaned;
    }
    if (typeof data.to === "string") {
      const cleaned = stripQueryAndHash(data.to);
      if (cleaned) data.to = cleaned;
    }
    if (typeof data.from === "string") {
      const cleaned = stripQueryAndHash(data.from);
      if (cleaned) data.from = cleaned;
    }
    // Drop POST/PUT/PATCH bodies that fetch breadcrumbs may attach.
    delete data.request_body_size;
    delete data.response_body_size;
  }
  return crumb;
}

// Init flag prevents double-init in HMR or accidental re-import.
let SENTRY_INITIALIZED = false;

export function initErrorReporting(): void {
  if (SENTRY_INITIALIZED) return;
  if (typeof window === "undefined") return;
  if (!DSN) {
    // Local dev / CI without a DSN: become a no-op. Crashes still
    // surface in the browser console + the Replit runtime-error
    // overlay; we just don't phone home.
    SENTRY_INITIALIZED = true;
    return;
  }
  Sentry.init({
    dsn: DSN,
    environment: ENVIRONMENT,
    release: `jingle-web@${APP_VERSION}`,
    // Default `sendDefaultPii: false` — keep IP / cookies off events.
    sendDefaultPii: false,
    // Performance + replay are intentionally OFF for v1: the founder
    // does not yet need transaction sampling, and session-replay would
    // capture rendered DOM (mission text!) which we explicitly never
    // send. Re-enable replay only with maskAllText/blockAllMedia.
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    integrations: [
      // Auto-installs window.error + unhandledrejection handlers,
      // which together replace the homegrown
      // `installGlobalErrorListeners()`.
      Sentry.globalHandlersIntegration({
        onerror: true,
        onunhandledrejection: true,
      }),
      Sentry.browserApiErrorsIntegration(),
      Sentry.linkedErrorsIntegration(),
      Sentry.dedupeIntegration(),
      Sentry.httpContextIntegration(),
      Sentry.breadcrumbsIntegration({
        console: true,
        dom: true,
        fetch: true,
        history: true,
        sentry: true,
        xhr: true,
      }),
    ],
    initialScope: {
      tags: {
        app: "jingle-web",
        appVersion: APP_VERSION,
      },
    },
    beforeSend(event) {
      return scrubEvent(event);
    },
    beforeBreadcrumb(crumb) {
      return scrubBreadcrumb(crumb);
    },
    // Common noise we explicitly never want:
    ignoreErrors: [
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
      "Non-Error promise rejection captured",
    ],
    denyUrls: [
      // Browser extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
      /^safari-extension:\/\//i,
    ],
  });
  SENTRY_INITIALIZED = true;
}

// Backwards-compatible alias: `main.tsx` calls this name today and the
// public API stays stable.
export function installGlobalErrorListeners(): void {
  initErrorReporting();
}

export interface ReportClientErrorInput {
  source: ClientErrorSource;
  error: unknown;
  componentStack?: string | null;
}

// Manual capture — used by the React ErrorBoundary's componentDidCatch
// to attach the React component stack alongside the error. The window /
// promise sources are picked up automatically by Sentry's global
// handlers, so callers only need this for the boundary case in practice.
export function reportClientError(input: ReportClientErrorInput): void {
  if (!SENTRY_INITIALIZED || !DSN) return;
  try {
    Sentry.withScope((scope) => {
      scope.setTag("error_source", input.source);
      if (input.componentStack) {
        const cleaned = scrubText(input.componentStack);
        if (cleaned) {
          scope.setContext("react", {
            componentStack: cleaned.slice(0, 4000),
          });
        }
      }
      const err = input.error;
      if (err instanceof Error) {
        Sentry.captureException(err);
      } else {
        const msg = scrubText(
          typeof err === "string"
            ? err
            : (() => {
                try {
                  return JSON.stringify(err);
                } catch {
                  return "Non-serializable error object";
                }
              })(),
        );
        Sentry.captureMessage(msg ?? "Unknown client error", "error");
      }
    });
  } catch {
    /* swallow — the reporter must never throw */
  }
}
